import { TabNavigationKeys } from "../enums/base.enums";
import { NavItem } from "../models/ui/profile-nav-item.model";

export interface TabOverflowLayout {
    visible: NavItem[];
    overflow: NavItem[];
}

export class ResponsiveTabsUtilities {

    /**
     * Splits a tab strip into the tabs that fit the bar and the tabs that belong in the overflow menu.
     *
     * Widths come from the caller's measurement of the rendered tabs rather than from any assumption
     * about label length: the app loads Inter with `display: swap`, so the same label is a different
     * width before and after the font arrives, and a tab's padding changes with the breakpoint.
     *
     * The active tab is always kept in the bar. A tab the user is currently looking at that hides
     * itself inside "More" reads as the strip having lost it, so when the active tab falls past the
     * fold, trailing tabs are given up until it fits.
     */
    public static split(
        items: NavItem[],
        measuredWidths: ReadonlyMap<string, number>,
        moreButtonWidth: number,
        availableWidth: number
    ): TabOverflowLayout {
        if (items.length === 0) {
            return { visible: [], overflow: [] };
        }

        // Nothing measured yet, or the bar is hidden behind a breakpoint and reports no width. Render
        // the whole set: that render is what supplies the measurements the next pass settles on.
        const isMeasured = items.every(item => measuredWidths.has(item.id));
        if (availableWidth <= 0 || !isMeasured) {
            return { visible: [...items], overflow: [] };
        }

        const widthOf = (item: NavItem): number => measuredWidths.get(item.id) ?? 0;

        const totalWidth = items.reduce((total, item) => total + widthOf(item), 0);
        if (totalWidth <= availableWidth) {
            return { visible: [...items], overflow: [] };
        }

        const budget = availableWidth - moreButtonWidth;
        const visible: NavItem[] = [];
        let used = 0;

        for (const item of items) {
            if (used + widthOf(item) > budget) break;
            used += widthOf(item);
            visible.push(item);
        }

        const activeItem = items.find(item => item.active);
        if (activeItem && !visible.includes(activeItem)) {
            used += widthOf(activeItem);
            while (visible.length > 0 && used > budget) {
                used -= widthOf(visible.pop()!);
            }
            visible.push(activeItem);
        }

        // A bar too narrow for even one tab still shows a tab rather than nothing at all.
        if (visible.length === 0) {
            visible.push(activeItem ?? items[0]);
        }

        const visibleIds = new Set(visible.map(item => item.id));
        return { visible, overflow: items.filter(item => !visibleIds.has(item.id)) };
    }

    /**
     * Where an arrow, Home or End key moves focus within the strip, or undefined for a key that is
     * none of the strip's business. Wraps at both ends, matching the Bootstrap tab plugin this
     * replaced so keyboard users keep the navigation they had.
     */
    public static nextTabIndex(key: string, currentIndex: number, tabCount: number): number | undefined {
        if (tabCount === 0) return undefined;

        switch (key) {
            case TabNavigationKeys.ArrowRight:
            case TabNavigationKeys.ArrowDown:
                return (currentIndex + 1) % tabCount;
            case TabNavigationKeys.ArrowLeft:
            case TabNavigationKeys.ArrowUp:
                return (currentIndex - 1 + tabCount) % tabCount;
            case TabNavigationKeys.Home:
                return 0;
            case TabNavigationKeys.End:
                return tabCount - 1;
            default:
                return undefined;
        }
    }
}
